import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { EMPTY, Observable, Subject, throwError as observableThrowError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { IbatchPagination } from '../../models/batch-pagination.model';
import { IBatch } from '../../models/batch.model';
import { Iproject } from '../../models/project.model';
import { IBatchForecastMeasurement } from '../../models/batch-forecast-measurement.model';
import { UpdateBatchStateModel } from "../../models/update-batch-state.model";
import { ToleranceViewerDto } from "../../models/viewmodels/tolerance-viewer-dto.model";

@Injectable()
export class ProjectService {
  private baseUrl: string;
  private simpleBaseUrl: string;

  // #508439 Update forecast measurements should only filter by productNr
  // used by forecast components to trigger retain sample measurements reload
  private retainSampleReloadSource = new Subject<void>();
  retainSampleReloadTrigger$ = this.retainSampleReloadSource.asObservable();

  constructor(public http: HttpClient) {
    this.baseUrl = environment.apiBasePath + environment.apiVersionPath;
    this.simpleBaseUrl = environment.apiBasePath;
  }

  // #508439 Update forecast measurements should only filter by productNr
  // used by forecast components to trigger retain sample measurements reload
  public triggerRetainSampleReload() {
    this.retainSampleReloadSource.next();
  }

  /**
   * Gets header information for a specific batch
   */
  public getBatchHeader(parameters: { batch: number; line: string }): Observable<Iproject> {
    if (!parameters.batch || !parameters.line) {
      return EMPTY;
    }
    const apiEndpoint = `${this.baseUrl}/${environment.batches.header
      .replace('{batchId}', parameters.batch.toString())
      .replace('{lineNumber}', parameters.line)}`;

    return this.http.get(apiEndpoint).pipe(
      map((response) => response as Iproject),
      catchError((error: HttpErrorResponse) => observableThrowError(error || error.error || 'Server error')),
    );
  }

  /**
   * Gets project information with array of products by query parameter
   *
   * @example
   * {
   *   "id": 4,
   *   "name": "Jatoba",
   *   "manufacturer": "BMW",
   *   "products": [{
   *     "name": "FW60-8B65",
   *     "type": "BODY"
   *   }]
   * }
   */
  public getProject(parameters: { projectId: string; lineId?: string; modelId?: string }): Observable<Iproject> {
    if (!parameters.projectId) {
      return EMPTY;
    }
    let apiEndpoint = `${this.baseUrl}/${environment.project.headerById.replace('{projectId}', parameters.projectId)}`;

    if (parameters.lineId) {
      apiEndpoint += '?lines=' + parameters.lineId;
    }

    if (parameters.lineId && parameters.modelId) {
      apiEndpoint += '&models=' + parameters.modelId;
    }

    return this.http.get(apiEndpoint).pipe(
      map((response) => response as Iproject),
      catchError((error: HttpErrorResponse) => observableThrowError(error || error.error || 'Server error')),
    );
  }

  /**
   * Gets pagination object with array of batches for a specified project id
   *
   * @example
   * {
   *   "totalElements":5,
   *   "totalPages":2,
   *   ...
   *   "content":[
   *      {
   *         "batch":{
   *            "id":11,
   *            "batchNumber":"0231658312",
   *            "kpis":[],
   *            "state":"IN_EXAMINATION",
   *            "lastMeasurementDate":null,
   *            "dockingDate":null
   *         },
   *         "productType":"BUMPER",
   *         "productName":"FW60-7B41",
   *         "productId":5
   *      }
   * ]}
   *
   * @params {String} projectId - productId
   * @params {String} lineId - lineId
   * @params {String} modelId - modelId
   * @params {String[]} selectedProducts - selected product names to filter // BCH-533622
   * @params {Number} page - pagination index
   * @params {Number} size - amount of items per page
   */
  public getBatches(parameters: {
    projectId: string;
    lineId: string;
    modelId: string;
    selectedProductIds?: number[];
    page: number;
    size: number;
  }): Observable<IbatchPagination> {
    if (!parameters.projectId) {
      return EMPTY;
    }

    const apiEndpoint = `${this.baseUrl}/${environment.batches.byProjectId.replace('{projectId}', parameters.projectId)}`;

    let params = new HttpParams();

    if (parameters.lineId) {
      params = params.set('lines', parameters.lineId);// BCH-533622
    }

    if (parameters.modelId) {
      params = params.set('models', parameters.modelId);// BCH-533622
    }

    if (parameters.selectedProductIds && parameters.selectedProductIds.length > 0) {
      params = params.set('productIds', parameters.selectedProductIds.join(',')); // BCH-533622
    }

    params = params.set('page', String(parameters.page));//BCH-533622
    params = params.set('size', String(parameters.size));//BCH-533622

    return this.http.get(apiEndpoint, { params }).pipe(
      map((response) => response as IbatchPagination),
      catchError((error: HttpErrorResponse) => observableThrowError(error || error.error || 'Server error')),
    );
  }

  /**
   * Gets forecast calculation for a batch combined with a reference batch and a measurement as parameter.
   */
  public getForecastBatchById(parameters: {
    id: number;
    reference: number;
    forecastMeasurementId: number;
    retainMeasurementId?: number
  }): Observable<IBatch> {
    if (!parameters.id || !parameters.reference || !parameters.forecastMeasurementId) {
      return EMPTY;
    }

    const apiEndpoint = `${this.baseUrl}/${environment.batches.forecastById.replace(
      '{batchId}',
      parameters.id.toString(),
    )}`;

    let params = new HttpParams();

    params = params.set('referenceBatchId', String(parameters.reference));
    params = params.set('forecastMeasurementId', String(parameters.forecastMeasurementId));
    if (parameters.retainMeasurementId) {
      params = params.set('retainMeasurementId', String(parameters.retainMeasurementId));
    }
    const requestOptions = {
      params,
    };

    return this.http.get(apiEndpoint, requestOptions).pipe(
      map((response) => response as IBatch),
      catchError((error: HttpErrorResponse) => observableThrowError(error || error.error || 'Server error')),
    );
  }

  public updateForecastByBatchId(parameters: { batchId: number }): Observable<number> {
    if (!parameters.batchId) {
      return EMPTY;
    }

    const apiEndpoint = `${this.simpleBaseUrl}/${environment.batches.updateForecastByBatchId}`;

    let params = new HttpParams();

    params = params.set('batchId', String(parameters.batchId));
    params = params.set('maxRows', String(99));

    const requestOptions = {
      params,
    };

    return this.http.get(apiEndpoint, requestOptions).pipe(
      map((response) => response as number),
      catchError((error: HttpErrorResponse) => observableThrowError(error || error.error || 'Server error')),
    );
  }

  public getForecastMeasurementsByBatchId(parameters: { batchId: number }): Observable<IBatchForecastMeasurement[]> {
    if (!parameters.batchId) {
      return EMPTY;
    }

    const apiEndpoint = `${this.baseUrl}/${environment.batches.forecastMeasurementsByBatchId.replace(
      '{batchId}',
      parameters.batchId.toString(),
    )}`;

    return this.http.get(apiEndpoint).pipe(
      map((response) => response as IBatchForecastMeasurement[]),
      catchError((error: HttpErrorResponse) => observableThrowError(error || error.error || 'Server error')),
    );
  }

  public getRetainMeasurementsByBatchId(parameters: { batchId: number }): Observable<IBatchForecastMeasurement[]> {
    if (!parameters.batchId) {
      return EMPTY;
    }

    const apiEndpoint = `${this.baseUrl}/${environment.batches.retainMeasurementsByBatchId.replace(
      '{batchId}',
      parameters.batchId.toString(),
    )}`;

    return this.http.get(apiEndpoint).pipe(
      map((response) => response as IBatchForecastMeasurement[]),
      catchError((error: HttpErrorResponse) => observableThrowError(error || error.error || 'Server error')),
    );
  }

  public updateRetainMeasurementsByBatchId(parameters: { batchId: number }): Observable<number> {
    if (!parameters.batchId) {
      return EMPTY;
    }

    const apiEndpoint = `${this.simpleBaseUrl}/${environment.batches.updateRetainMeasurementsByBatchId}`;

    let params = new HttpParams();

    params = params.set('batchId', String(parameters.batchId));
    params = params.set('maxRows', String(99));

    const requestOptions = {
      params,
    };

    return this.http.get(apiEndpoint, requestOptions).pipe(
      map((response) => response as number),
      catchError((error: HttpErrorResponse) => observableThrowError(error || error.error || 'Server error')),
    );
  }

  public updateLabMeasurementsByProductId(parameters: { productId: number }): Observable<number> {
    if (!parameters.productId) {
      return EMPTY;
    }

    const apiEndpoint = `${this.simpleBaseUrl}/${environment.batches.updateLabMeasurements}`;

    let params = new HttpParams();

    params = params.set('productId', String(parameters.productId));
    params = params.set('maxRows', String(99));

    const requestOptions = {
      params,
    };

    return this.http.get(apiEndpoint, requestOptions).pipe(
      map((response) => response as number),
      catchError((error: HttpErrorResponse) => observableThrowError(error || error.error || 'Server error')),
    );
  }

  public updateBatchState(parameters: { productId: string; }): Observable<UpdateBatchStateModel> {
    if (!parameters.productId) {
      return EMPTY;
    }
    let apiEndpoint = `${this.baseUrl}/${environment.project.updateBatchStateByProductId.replace('{productId}', parameters.productId)}`;

    return this.http.get(apiEndpoint).pipe(
      map((response) => response as UpdateBatchStateModel),
      catchError((error: HttpErrorResponse) => observableThrowError(error || error.error || 'Server error')),
    );
  }

  // #487495 - Tolerance viewer
  public getTolerancesByProject(projectId: string): Observable<ToleranceViewerDto> {
    if (!projectId) {
      return EMPTY;
    }
    let apiEndpoint = `${this.baseUrl}/${environment.project.tolerancesById.replace('{projectId}', projectId)}`;

    return this.http.get(apiEndpoint).pipe(
      map((response) => response as ToleranceViewerDto),
      catchError((error: HttpErrorResponse) => observableThrowError(error || error.error || 'Server error')),
    );
  }

  public getTolerancesByBatch(batchId: string): Observable<ToleranceViewerDto> {
    if (!batchId) {
      return EMPTY;
    }
    let apiEndpoint = `${this.baseUrl}/${environment.project.tolerancesByBatchId.replace('{batchId}', batchId)}`;

    return this.http.get(apiEndpoint).pipe(
      map((response) => response as ToleranceViewerDto),
      catchError((error: HttpErrorResponse) => observableThrowError(error || error.error || 'Server error')),
    );
  }
}
